/*
<summary>
   This class component is all about Managing Tool Management data.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/

import { action, computed, makeObservable, observable } from 'mobx';
import Papa from 'papaparse';
import toast from 'react-hot-toast';
import { fixTimezoneOffsetYYYYMMDD } from '../../constants/constants';
import URLConstants from '../../constants/url-constants';
import {
  IAddEditApplicatorMountingRequest,
  IAddToolManagement,
  IBorrowToolRequest,
  ILoanOutToolRequest,
  IUnmountApplicatorRequest,
  IUpdateToolLoanDetailsRequest,
  IUpdateToolManagement,
  IValidateToolRequest,
} from '../../models/forms/IAddUpdateToolManagement';
import {
  ICrimpMasterCSVImportResult,
  IToolCheckItem,
  IToolCrimpRow,
  IToolInspectionTab,
} from '../../models/forms/IToolInspection';
import { IOption } from '../../models/ICommon';
import { IToolManagementFilter } from '../../models/IToolManagementFilter';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  IToolLoanDetailsVM,
  IToolManagementCSVImportResult,
  IToolManagementList,
  IToolManagementListVM,
  IToolManagementVM,
  IToolTrayUsageInfo,
} from '../../models/response/IToolManagementResponse';
import { formatMessage } from '../../translations/format-message';
import {
  AddEditToolManagementInitialState,
  GetAllToolManagementState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import { downloadCsvFromRows } from './utils/download-csv';

export class ToolManagementStore {
  initialStateValue = ObservableInitialState;
  toolManagementList: IToolManagementListVM = GetAllToolManagementState;
  toolManagement: IToolManagementVM | undefined = undefined;
  toolManagementState = { ...this.initialStateValue };
  addUpdateToolManagementState = { ...this.initialStateValue };
  deleteToolManagementState = { ...this.initialStateValue };
  getAllToolManagementState = { ...this.initialStateValue };
  fileUploadState = { ...this.initialStateValue };
  exportFileState = { ...this.initialStateValue };
  returnToolState = { ...this.initialStateValue };
  loanOutToolState = { ...this.initialStateValue };
  validateToolState = { ...this.initialStateValue };
  borrowToolState = { ...this.initialStateValue };
  toolLoanDetails: IToolLoanDetailsVM | undefined = undefined;
  toolLoanDetailsState = { ...this.initialStateValue };
  importResults: IToolManagementCSVImportResult[] = [];
  base64String: any = { fileString: '' };
  addEditApplicatorMountingState = { ...this.initialStateValue };
  mainUnitOptions: IOption[] = [];
  mainUnitList: {
    ToolId: number;
    ToolManagementNo: string;
    ToolName: string;
  }[] = [];
  mainUnitOptionsState = { ...this.initialStateValue };
  unmountApplicatorState = { ...this.initialStateValue };
  trayIdsByTool: IToolTrayUsageInfo[] = [];
  trayIdsByToolState = { ...this.initialStateValue };
  toolLedBlinkState = { ...this.initialStateValue };

  constructor() {
    makeObservable(this, {
      toolManagement: observable,
      toolManagementState: observable,
      toolManagementList: observable,
      addUpdateToolManagementState: observable,
      deleteToolManagementState: observable,
      getAllToolManagementState: observable,
      fileUploadState: observable,
      exportFileState: observable,
      returnToolState: observable,
      loanOutToolState: observable,
      validateToolState: observable,
      borrowToolState: observable,
      toolLoanDetails: observable,
      toolLoanDetailsState: observable,
      importResults: observable,
      base64String: observable,
      addEditApplicatorMountingState: observable,
      mainUnitOptions: observable,
      mainUnitList: observable,
      mainUnitOptionsState: observable,
      unmountApplicatorState: observable,
      trayIdsByTool: observable,
      trayIdsByToolState: observable,
      toolLedBlinkState: observable,
      GetTrayIdsByToolManagementNoService: action,
      resetTrayIdsByTool: action,
      GetToolManagementListService: action,
      GetToolManagementByIdService: action,
      AddToolManagementService: action,
      UpdateToolManagementService: action,
      DeleteToolManagementService: action,
      ToolManagementFileUploadService: action,
      ExportToolManagementFileService: action,
      LoanOutToolService: action,
      ReturnToolService: action,
      GetToolLoanDetailsByToolIdService: action,
      BorrowToolService: action,
      UpdateCalibrationExpiryService: action,
      AddToolTempFilesService: action,
      AddEditApplicatorMountingService: action,
      GetAllAvailableMainUnitsService: action,
      UnmountApplicatorService: action,
      ToolLedBlinkService: action,
      resetDeleteToolManagement: action,
      resetAddUpdateToolManagementState: action,
      resetToolManagementDetail: action,
      resetFileUploadState: action,
      resetExportFileState: action,
      resetReturnToolState: action,
      resetToolLoanDetailsState: action,
      resetLoanOutToolState: action,
      resetValidateToolState: action,
      resetBorrowToolState: action,
      reset: action,
      allToolManagements: computed,
      toolManagementDetails: computed,
      resetStore: action,
      resetToolLedBlinkState: action,
    });
  }

  //#region Actions
  /**
   * This function is used to get the paginated Tool Management list from API.
   * @param pageNo: Page Number
   * @param pageSize: Page Size / No of records in a page
   * @param toolType: Selected tab (General Tool/Main Unit/Applicator) — ToolTypeEnum value;
   * omitted when the caller wants tools across every tab (e.g. looking a tool up by id)
   * @param filter: Filter values; omitted/undefined fields aren't sent to the API
   * @param actionType: 1 = no filter applied (default), 2 = filter applied
   */
  GetToolManagementListService = (
    pageNo: number,
    pageSize: number,
    toolType?: number,
    filter?: IToolManagementFilter,
    actionType: number = 1
  ) => {
    this.getAllToolManagementState.inProgress = true;
    const params = new URLSearchParams();
    params.set('PageNo', String(pageNo));
    params.set('PageSize', String(pageSize));
    if (toolType) params.set('ToolType', String(toolType));
    params.set('ActionType', String(actionType));
    if (filter) {
      if (filter.OwnerFactoryId && filter.OwnerFactoryId !== '-1') {
        params.set('OwnerFactoryId', filter.OwnerFactoryId);
      }
      if (filter.ToolManagementNo)
        params.set('ToolManagementNo', filter.ToolManagementNo);
      if (filter.ToolName) params.set('ToolName', filter.ToolName);
      if (filter.SerialNo) params.set('SerialNo', filter.SerialNo);
      if (filter.InspectionStatus && filter.InspectionStatus !== '-1') {
        params.set('InspectionStatus', filter.InspectionStatus);
      }
      if (filter.CalibrationStatus && filter.CalibrationStatus !== '-1') {
        params.set('CalibrationStatus', filter.CalibrationStatus);
      }
      if (filter.UsageStatus && filter.UsageStatus !== '-1') {
        params.set('UsageStatus', filter.UsageStatus);
      }
      if (filter.Column) params.set('Column', filter.Column);
      if (filter.Order) params.set('OrderBy', filter.Order);
    }
    const url = `${URLConstants.GetAllToolManagementList}?${params.toString()}`;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IToolManagementListVM>>
        ) => {
          if (response.data.Error) {
            this.getAllToolManagementState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.toolManagementList = response.data.Data;
            this.getAllToolManagementState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getAllToolManagementState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getAllToolManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to add a new tool via the Add Tool Management API.
   * @param data: Tool Management Details
   */
  AddToolManagementService = (data: IAddToolManagement) => {
    this.addUpdateToolManagementState.inProgress = true;
    const url = URLConstants.AddToolManagement;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateToolManagementState.error = response.data.Message;
        } else {
          this.addUpdateToolManagementState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateToolManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateToolManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to update an existing tool via the Update Tool Management API.
   * @param data: Tool Management Details
   */
  UpdateToolManagementService = (data: IUpdateToolManagement) => {
    this.addUpdateToolManagementState.inProgress = true;
    const url = URLConstants.UpdateToolManagement;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateToolManagementState.error = response.data.Message;
        } else {
          this.addUpdateToolManagementState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateToolManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateToolManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to map the raw Get Tool Management By Id API response into
   * the view-model shape the Add/Edit form expects (inspection tabs, headers, crimp rows).
   * @param data: Raw API response data
   */
  mapGetToolManagementByIdResponse = (data: any): IToolManagementVM => {
    if (!data)
      return AddEditToolManagementInitialState as unknown as IToolManagementVM;

    const tabsMap = new Map<number, IToolInspectionTab>();

    (data.uciFiles || []).forEach((file: any) => {
      const tabIndex = file.TabIndex || file.TabOrder || 1;
      if (!tabsMap.has(tabIndex)) {
        tabsMap.set(tabIndex, {
          TabIndex: tabIndex,
          Title: file.Heading || `Tab ${tabIndex}`,
          Images: [],
          CheckItems: [],
          Headers: [],
        });
      }
      const tab = tabsMap.get(tabIndex)!;
      if (file.Heading) tab.Title = file.Heading;
      tab.Images.push({
        Id: file.Id,
        FileId: file.TempId || file.Id,
        FileName: file.FileName || '',
        FilePath: file.S3FilePath || '',
        S3FilePath: file.S3FilePath || '',
        PreviewUrl: file.S3FilePath || '',
      });
    });

    const tabHeaderMap = new Map<
      number,
      Map<
        string,
        { Title: string; HeaderOrder: number; Items: IToolCheckItem[] }
      >
    >();

    (data.uciCheckItem || []).forEach((ci: any) => {
      const tabIndex = ci.TabIndex || 1;
      if (!tabsMap.has(tabIndex)) {
        tabsMap.set(tabIndex, {
          TabIndex: tabIndex,
          Title: `Tab ${tabIndex}`,
          Images: [],
          CheckItems: [],
          Headers: [],
        });
      }
      const tab = tabsMap.get(tabIndex)!;
      const headerTitle = ci.HeaderName || ci.CheckHeader || 'Default';
      // HeaderOrder is the sequence the user arranged headers in (via the Check Items panel's
      // Move Up/Down), persisted so headers reload in that order instead of whatever order
      // their rows happen to arrive in.
      const headerOrder = ci.HeaderOrder ?? 0;

      if (!tabHeaderMap.has(tabIndex)) {
        tabHeaderMap.set(tabIndex, new Map());
      }
      const headersOfTab = tabHeaderMap.get(tabIndex)!;
      if (!headersOfTab.has(headerTitle)) {
        headersOfTab.set(headerTitle, {
          Title: headerTitle,
          HeaderOrder: headerOrder,
          Items: [],
        });
      }
      const headerGroup = headersOfTab.get(headerTitle)!;
      const checkItem: IToolCheckItem = {
        Id: ci.Id,
        CheckItemMessage: ci.CheckitemText || '',
        IsChecked: !!ci.AllowTextEntry,
        HeaderTitle: headerTitle,
        AllowTextEntry: !!ci.AllowTextEntry,
      };
      headerGroup.Items.push(checkItem);
      tab.CheckItems.push(checkItem);
    });

    tabsMap.forEach((tab, tabIndex) => {
      if (tabHeaderMap.has(tabIndex)) {
        tab.Headers = Array.from(tabHeaderMap.get(tabIndex)!.values()).sort(
          (a, b) => a.HeaderOrder - b.HeaderOrder
        );
      }
    });

    const inspectionTabs = Array.from(tabsMap.values()).sort(
      (a, b) => a.TabIndex - b.TabIndex
    );

    const crimpRows = (data.uciCrimpMaster || []).map(
      (c: any, index: number) => ({
        Id: c.Id,
        RowIndex: index + 1,
        AWG: c.Awg || '',
        UpperLimitCH: c.UpperLimit || 0,
        LowerLimitCH: c.LowerLimit || 0,
        TensileStrengthLimit: c.TensileStrength || 0,
      })
    );

    // Dropdowns default to a "Please Select" sentinel when a field is empty, so a
    // null/blank value coming back from the API must resolve to that base value.
    const normalized: any = { ...data };
    if (!normalized.EquipmentCategory) {
      normalized.EquipmentCategory = '';
    }
    const PLEASE_SELECT_NUMBER_FIELDS: (keyof IToolManagementVM)[] = [
      'UsageStatus',
      'InspectionStatus',
      'CalibrationStatus',
      'InspectionFormat',
      'InspectionCycle',
    ];
    PLEASE_SELECT_NUMBER_FIELDS.forEach((field) => {
      if (
        normalized[field] === null ||
        normalized[field] === undefined ||
        normalized[field] === ('' as unknown)
      ) {
        normalized[field] = -1;
      }
    });
    // ProcessUsed's <select> submits the process name itself (getData prop), so its
    // "please_select" placeholder option value is that literal string, not -1.
    if (
      normalized.ProcessUsed === null ||
      normalized.ProcessUsed === undefined ||
      normalized.ProcessUsed === ('' as unknown)
    ) {
      normalized.ProcessUsed = 'please_select';
    }

    // The date pickers expect a local date, not the raw UTC timestamp the API
    // returns, so run each date field through the same fixTimezoneOffsetYYYYMMDD
    // conversion used elsewhere in the project (e.g. allToolManagements below).
    const DATE_FIELDS: (keyof IToolManagementVM)[] = [
      'CalibrationExpiryDate',
      'PurchaseDate',
      'DisposalDate',
      'LoanDate',
      'PlannedReturnDate',
    ];
    DATE_FIELDS.forEach((field) => {
      const value = normalized[field];
      if (value) {
        (normalized[field] as unknown as string) =
          fixTimezoneOffsetYYYYMMDD(value);
      }
    });

    // uciFiles/uciCheckItem/uciCrimpMaster are the raw GetToolManagementMasterById response
    // fields already fully consumed above into InspectionTabs/CrimpRows — drop them so they
    // don't ride along on the form's values (and, from there, risk leaking into a request body).
    delete normalized.uciFiles;
    delete normalized.uciCheckItem;
    delete normalized.uciCrimpMaster;

    return {
      ...normalized,
      InspectionTabs: inspectionTabs,
      CrimpRows: crimpRows,
    };
  };

  /**
   * This function is used to get Tool Management Data Details by Id from API.
   * @param id: Tool Identifier
   */
  GetToolManagementByIdService = (id: number) => {
    this.toolManagementState.inProgress = true;
    const url = `${URLConstants.GetToolManagementById}?toolId=${id}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.toolManagementState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.toolManagement = this.mapGetToolManagementByIdResponse(
            response.data.Data
          );
        }
      })
      .catch((err: string) => {
        this.toolManagementState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.toolManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to mark a tool as inactive/disposed via the Delete Tool Management API.
   * @param id: Tool Identifier
   * @param toolNumber: Tool Management No of the tool being deleted
   * @param tenantId: Tenant Identifier
   */
  DeleteToolManagementService = (
    id: number,
    toolNumber: string,
    tenantId: number
  ) => {
    this.deleteToolManagementState.inProgress = true;
    const url = URLConstants.DeleteToolManagement;
    const requestBody = {
      Id: id,
      IsActive: false,
      ToolNumber: toolNumber,
      TenantId: tenantId,
    };
    return baseService
      .putRequest(url, requestBody)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteToolManagementState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteToolManagementState.success = true;
      })
      .catch((err: string) => {
        this.deleteToolManagementState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.deleteToolManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to upload a CSV file for bulk Tool Management import.
   * @param tenantId: Tenant Identifier
   * @param csvFile: CSV File to be uploaded
   */
  ToolManagementFileUploadService = (tenantId: number, csvFile: File) => {
    this.fileUploadState.inProgress = true;
    const url = `${URLConstants.UploadToolManagementFile}?TenantId=${tenantId}`;
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    return baseService
      .postMultipart(url, formData, {})
      .then(
        (
          response: IApiResponse<
            IApiSuccessResponse<IToolManagementCSVImportResult[]>
          >
        ) => {
          const results = response.data.Data || [];
          this.importResults = results;
          if (results.length > 0) {
            downloadCsvFromRows(
              [
                formatMessage('row_no'),
                formatMessage('tool_management_no'),
                formatMessage('status'),
                formatMessage('message'),
              ],
              results.map((row) => [
                row.RowNo,
                row.ToolManagementNo || '',
                row.Success
                  ? formatMessage('success')
                  : formatMessage('failed'),
                formatMessage(row.Message),
              ]),
              'ToolManagementImportResult'
            );
          }
          if (response.data.Error) {
            this.fileUploadState.error = response.data.Message;
          } else {
            this.fileUploadState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.fileUploadState.error = err;
      })
      .finally(
        action(() => {
          this.fileUploadState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to loan out a tool via the Loan Out Tool API.
   * @param data: Loan Out Tool Request Details
   */
  LoanOutToolService = (data: ILoanOutToolRequest) => {
    this.loanOutToolState.inProgress = true;
    const url = URLConstants.LoanOutTool;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.loanOutToolState.error = response.data.Message;
        } else {
          this.loanOutToolState.success = true;
        }
      })
      .catch((err: string) => {
        this.loanOutToolState.error = err;
      })
      .finally(
        action(() => {
          this.loanOutToolState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to get the tray ids a tool has been used/scanned against, via the
   * Get Tray Ids By Tool Management No API.
   * @param toolManagementNo: Tool Management Number
   * @param tenantId: Tenant Identifier
   */
  GetTrayIdsByToolManagementNoService = (
    toolManagementNo: string,
    tenantId: number
  ) => {
    this.trayIdsByToolState.inProgress = true;
    this.trayIdsByToolState.error = '';
    this.trayIdsByToolState.success = false;
    const url = `${URLConstants.GetTrayIdsByToolManagementNo}?toolManagementNo=${encodeURIComponent(toolManagementNo || '')}&tenantId=${tenantId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IToolTrayUsageInfo[]>>) => {
        if (response.data.Error) {
          this.trayIdsByToolState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.trayIdsByTool = response.data.Data || [];
          this.trayIdsByToolState.success = true;
        }
      })
      .catch((err: string) => {
        this.trayIdsByToolState.error = err;
      })
      .finally(
        action(() => {
          this.trayIdsByToolState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to reset trayIdsByTool and trayIdsByToolState observables to their
   * initial values.
   */
  resetTrayIdsByTool = () => {
    this.trayIdsByTool = [];
    this.trayIdsByToolState = { ...this.initialStateValue };
  };

  /**
   * This function is used to get the current loan record for a tool via the Get Tool Loan
   * Details By Tool Id API.
   * @param toolId: Tool Identifier
   */
  GetToolLoanDetailsByToolIdService = (toolId: number) => {
    this.toolLoanDetailsState.inProgress = true;
    const url = `${URLConstants.GetToolLoanDetailsByToolId}?toolId=${toolId}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IToolLoanDetailsVM>>) => {
          if (response.data.Error) {
            this.toolLoanDetailsState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.toolLoanDetails = response.data.Data;
            this.toolLoanDetailsState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.toolLoanDetailsState.error = err;
      })
      .finally(
        action(() => {
          this.toolLoanDetailsState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to return a loaned out tool via the Update Tool Loan Details API.
   * @param data: Update Tool Loan Details Request
   */
  ReturnToolService = (data: IUpdateToolLoanDetailsRequest) => {
    this.returnToolState.inProgress = true;
    const url = URLConstants.ReturnTool;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.returnToolState.error = response.data.Message;
        } else {
          this.returnToolState.success = true;
        }
      })
      .catch((err: string) => {
        this.returnToolState.error = err;
      })
      .finally(
        action(() => {
          this.returnToolState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to assign a tag to a loaned-out tool via the Borrow Tool API.
   * @param data: Borrow Tool Request Details
   */
  BorrowToolService = (data: IBorrowToolRequest) => {
    this.borrowToolState.inProgress = true;
    const url = URLConstants.BorrowTool;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.borrowToolState.error = response.data.Message;
        } else {
          this.borrowToolState.success = true;
        }
      })
      .catch((err: string) => {
        this.borrowToolState.error = err;
      })
      .finally(
        action(() => {
          this.borrowToolState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to update a tool's calibration expiry date via the Validate Tool API.
   * @param data: Validate Tool Request Details
   */
  UpdateCalibrationExpiryService = (data: IValidateToolRequest) => {
    this.validateToolState.inProgress = true;
    const url = URLConstants.ValidateTool;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.validateToolState.error = response.data.Message;
        } else {
          this.validateToolState.success = true;
        }
      })
      .catch((err: string) => {
        this.validateToolState.error = err;
      })
      .finally(
        action(() => {
          this.validateToolState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to export the Tool Management list as a CSV file from API
   * @param tenantId: Tenant Identifier
   * @param filters: Filter & Sort Query String
   * @param language: UI language ('en'/'jp')
   */
  ExportToolManagementFileService = (
    tenantId: number,
    filters?: string,
    language?: string
  ) => {
    this.exportFileState.inProgress = true;
    const url = `${URLConstants.ExportToolManagementFile}?TenantId=${tenantId}&Filters=${filters}&Language=${encodeURIComponent(language || '')}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<string>>) => {
        if (response.data.Error) {
          this.exportFileState.error = response.data.Message;
        } else {
          this.exportFileState.success = true;
          this.base64String = { fileString: response.data.Data };
        }
      })
      .catch((err: string) => {
        this.exportFileState.error = err;
      })
      .finally(
        action(() => {
          this.exportFileState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to upload a temporary tool inspection file via API.
   * @param file: File to be uploaded
   * @param createdBy: User Identifier who is uploading the file
   */
  AddToolTempFilesService = (file: File, createdBy: number = 1) => {
    const url = URLConstants.AddToolTempFiles;
    const formData = new FormData();
    formData.append('CreatedBy', createdBy.toString());
    formData.append('File', file);
    return baseService
      .postMultipart(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          toast.error(formatMessage(response.data.Message));
          return null;
        } else {
          return response.data.Data;
        }
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
        return null;
      });
  };

  /**
   * This function is used to mount/swap the Applicator mounted on a Main Unit tool via the
   * Add Swap Applicator API.
   * @param payload: Main Unit Tool Id, selected Applicator Tool Id and the acting user
   */
  AddEditApplicatorMountingService = (
    payload: IAddEditApplicatorMountingRequest
  ) => {
    this.addEditApplicatorMountingState.inProgress = true;
    const url = URLConstants.AddSwapApplicator;
    return baseService
      .postRequest(url, payload)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          toast.error(formatMessage(response.data.Message));
          this.addEditApplicatorMountingState.error = response.data.Message;
          return false;
        }
        this.addEditApplicatorMountingState.success = true;
        return true;
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
        this.addEditApplicatorMountingState.error = err;
        return false;
      })
      .finally(
        action(() => {
          this.addEditApplicatorMountingState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to get the dropdown option list of available (not currently mounted)
   * Main Unit tools for the "Applicator Setting" popup, when it's opened from the Applicator
   * side (Mounted Main Unit column), via the Get All Available Main Units API.
   * @param ownerFactoryId: scopes the result to main units usable by this factory (owned by it
   * and not loaned out, or currently on loan to it)
   */
  GetAllAvailableMainUnitsService = (ownerFactoryId: number) => {
    this.mainUnitOptionsState.inProgress = true;
    const url = `${URLConstants.GetAllAvailableMainUnits}?ownerFactoryId=${ownerFactoryId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any[]>>) => {
        if (response.data.Error) {
          this.mainUnitOptionsState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
          return;
        }
        const data = response.data.Data || [];
        this.mainUnitList = data;
        this.mainUnitOptions = data.map((mainUnit) => ({
          id: mainUnit.ToolId,
          value: `${mainUnit.ToolManagementNo} - ${mainUnit.ToolName}`,
          disabled: false,
        }));
        this.mainUnitOptionsState.success = true;
      })
      .catch((err: string) => {
        this.mainUnitOptionsState.error = err;
      })
      .finally(
        action(() => {
          this.mainUnitOptionsState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to unmount an Applicator from whichever Main Unit it's currently
   * mounted on, via the Unmount Applicator API.
   * @param payload: Applicator Tool Id and the acting user
   */
  UnmountApplicatorService = (payload: IUnmountApplicatorRequest) => {
    this.unmountApplicatorState.inProgress = true;
    const url = URLConstants.UnmountApplicator;
    return baseService
      .postRequest(url, payload)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          toast.error(formatMessage(response.data.Message));
          this.unmountApplicatorState.error = response.data.Message;
          return false;
        }
        this.unmountApplicatorState.success = true;
        return true;
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
        this.unmountApplicatorState.error = err;
        return false;
      })
      .finally(
        action(() => {
          this.unmountApplicatorState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to parse a crimp default-values CSV — so this validates each row
   * with the same rules the manual row inputs already enforce checking AWG duplicates against
   * both the CSV itself and the rows already in the table, and never touches the network.
   * @param file: CSV file selected by the user
   * @param existingRows: Crimp rows already in the table, to continue the row numbering from
   * and to check AWG duplicates against
   */
  ParseCrimpMasterCSVFile = (
    file: File,
    existingRows: IToolCrimpRow[]
  ): Promise<{
    successRows: IToolCrimpRow[];
    failedRows: ICrimpMasterCSVImportResult[];
  }> => {
    return new Promise((resolve) => {
      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const successRows: IToolCrimpRow[] = [];
          const failedRows: ICrimpMasterCSVImportResult[] = [];
          const seenAwg = new Set(
            existingRows
              .map((row) => (row.AWG || '').trim().toLowerCase())
              .filter(Boolean)
          );
          let nextIndex = existingRows.reduce(
            (max, row) => Math.max(max, row.RowIndex),
            0
          );

          // Accepts either the English or Japanese CSV Template header row (both are offered
          // for download depending on the UI language), so a file downloaded under one
          // language still imports correctly regardless of which language is active later.
          const getField = (
            raw: Record<string, string>,
            ...keys: string[]
          ): string => {
            for (const key of keys) {
              if (raw[key]) return raw[key].trim();
            }
            return '';
          };

          result.data.forEach((raw, index) => {
            // CSV header is row 1, so the first data row is row 2.
            const rowNo = index + 2;
            const awg = getField(raw, 'AWG');
            const upperRaw = getField(raw, 'UpperLimitCH(mm)', '上限CH(mm)');
            const lowerRaw = getField(raw, 'LowerLimitCH(mm)', '下限CH(mm)');
            const tensileRaw = getField(
              raw,
              'TensileStrengthLimit(N)',
              '引張強度下限(N)'
            );

            const fail = (message: string) => {
              failedRows.push({ RowNo: rowNo, Awg: awg, Message: message });
            };

            if (!awg) {
              fail('please_enter_awg');
              return;
            }
            if (seenAwg.has(awg.toLowerCase())) {
              fail('duplicate_awg_not_allowed');
              return;
            }
            if (!upperRaw || isNaN(Number(upperRaw))) {
              fail('please_enter_upper_limit');
              return;
            }
            const upper = Number(upperRaw);
            if (!lowerRaw || isNaN(Number(lowerRaw))) {
              fail('please_enter_lower_limit');
              return;
            }
            const lower = Number(lowerRaw);
            if (upper < lower) {
              fail('upper_limit_less_than_lower');
              return;
            }
            if (!tensileRaw || isNaN(Number(tensileRaw))) {
              fail('please_enter_tensile_strength');
              return;
            }
            const tensile = Number(tensileRaw);
            if (!Number.isInteger(tensile) || tensile <= 0) {
              fail('tensile_strength_must_be_positive_integer');
              return;
            }

            seenAwg.add(awg.toLowerCase());
            nextIndex += 1;
            successRows.push({
              RowIndex: nextIndex,
              AWG: awg,
              UpperLimitCH: upper,
              LowerLimitCH: lower,
              TensileStrengthLimit: tensile,
            });
          });

          resolve({ successRows, failedRows });
        },
      });
    });
  };

  ToolLedBlinkService = (tenantId: number, toolId: string, data: any) => {
    this.toolLedBlinkState.inProgress = true;
    const url = `${URLConstants.ToolLedBlink}?TenantId=${tenantId}&ToolManagementNo=${toolId}&ColorCode=${encodeURIComponent(data.ColorCode)}&Second=${data.Second}`;
    return baseService
      .postRequest(url, null)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          toast.error(formatMessage(response.data.Message));
          this.toolLedBlinkState.error = response.data.Message;
        } else {
          this.toolLedBlinkState.success = true;
        }
      })
      .catch((err: string) => {
        this.toolLedBlinkState.error = err;
      })
      .finally(
        action(() => {
          this.toolLedBlinkState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region Computed
  /**
   * This computed property is used to map toolManagementList to a list suitable for the Grid Component.
   * @returns Tool Management List Details
   */
  get allToolManagements(): IToolManagementList[] {
    let toolManagementData: IToolManagementList[] = [];
    if (
      this.toolManagementList?.Tools &&
      this.toolManagementList.Tools?.length > 0
    ) {
      this.toolManagementList.Tools.map((tool) => {
        const withDates = this.mapDateFields(
          tool as unknown as IToolManagementList
        );
        toolManagementData.push(this.mapEmptyDropdownValues(withDates));
      });
    }
    return toolManagementData;
  }

  /**
   * This function is used to convert a tool row's date fields to the tenant's local
   * date using fixTimezoneOffsetYYYYMMDD.
   * @param tool: Tool Management List Row
   */
  private mapDateFields = (tool: IToolManagementList): IToolManagementList => {
    const DATE_FIELDS: (keyof IToolManagementList)[] = [
      'LoanDate',
      'PlannedReturnDate',
      'CalibrationExpiryDate',
      'PurchaseDate',
      'DisposalDate',
    ];
    const normalized: IToolManagementList = { ...tool };
    DATE_FIELDS.forEach((field) => {
      const value = normalized[field] as unknown as string | null;
      if (value) {
        (normalized[field] as unknown as string) =
          fixTimezoneOffsetYYYYMMDD(value);
      }
    });
    return normalized;
  };

  /**
   * This function is used to normalize a tool row's dropdown-driven fields so blank/sentinel
   * values render as the grid's shared "-" empty state.
   * @param tool: Tool Management List Row
   */
  private mapEmptyDropdownValues = (
    tool: IToolManagementList
  ): IToolManagementList => {
    const normalized: IToolManagementList = { ...tool };
    if (!normalized.ProcessUsed || normalized.ProcessUsed === 'please_select') {
      normalized.ProcessUsed = null;
    }
    return normalized;
  };

  /**
   * This computed property provides initial values to the Add Update Tool Management Form.
   * @returns Tool Management Details
   */
  get toolManagementDetails(): IToolManagementVM {
    if (this.toolManagement) {
      const tool = this.toolManagement;
      const emptyStringFallback = (value: string | null | undefined) =>
        value || '';
      return {
        ...tool,
        ToolManagementNo: emptyStringFallback(tool.ToolManagementNo),
        ToolName: emptyStringFallback(tool.ToolName),
        ToolManufacturer: emptyStringFallback(tool.ToolManufacturer),
        ToolModel: emptyStringFallback(tool.ToolModel),
        SerialNo: emptyStringFallback(tool.SerialNo),
        EquipmentCategory: emptyStringFallback(tool.EquipmentCategory),
        Location: emptyStringFallback(tool.Location),
        ManagingDepartment: emptyStringFallback(tool.ManagingDepartment),
        ResponsibleManager: emptyStringFallback(tool.ResponsibleManager),
        FixedAssetNo: emptyStringFallback(tool.FixedAssetNo),
        TagId: emptyStringFallback(tool.TagId),
        TeachmebizUrl: emptyStringFallback(tool.TeachmebizUrl),
        Remarks: emptyStringFallback(tool.Remarks),
        PartsManufacturer: emptyStringFallback(tool.PartsManufacturer),
        ReelModel: emptyStringFallback(tool.ReelModel),
        LooseTerminalModel: emptyStringFallback(tool.LooseTerminalModel),
      };
    }
    return {
      ToolId: -1,
      ...AddEditToolManagementInitialState,
    } as unknown as IToolManagementVM;
  }
  //#endregion

  //#region Reset
  reset = () => {
    this.toolManagementList = GetAllToolManagementState;
  };

  resetToolManagementDetail = () => {
    this.toolManagement = undefined;
    this.toolManagementState = { ...this.initialStateValue };
  };

  resetDeleteToolManagement = () => {
    this.deleteToolManagementState = { ...this.initialStateValue };
  };

  resetAddUpdateToolManagementState = () => {
    this.addUpdateToolManagementState.inProgress = false;
    this.addUpdateToolManagementState.success = false;
    this.addUpdateToolManagementState.error = '';
  };

  resetFileUploadState = () => {
    this.fileUploadState = { ...this.initialStateValue };
    this.importResults = [];
  };

  resetExportFileState = () => {
    this.exportFileState = { ...this.initialStateValue };
    this.base64String = { fileString: '' };
  };

  resetReturnToolState = () => {
    this.returnToolState = { ...this.initialStateValue };
  };

  resetToolLoanDetailsState = () => {
    this.toolLoanDetails = undefined;
    this.toolLoanDetailsState = { ...this.initialStateValue };
  };

  resetLoanOutToolState = () => {
    this.loanOutToolState = { ...this.initialStateValue };
  };

  resetValidateToolState = () => {
    this.validateToolState = { ...this.initialStateValue };
  };

  resetBorrowToolState = () => {
    this.borrowToolState = { ...this.initialStateValue };
  };

  resetToolLedBlinkState = () => {
    this.toolLedBlinkState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset all store observables to their initial values.
   * @returns
   */
  resetStore = () => {
    this.toolManagement = undefined;
    this.toolManagementState = { ...this.initialStateValue };
    this.toolManagementList = GetAllToolManagementState;
    this.addUpdateToolManagementState = { ...this.initialStateValue };
    this.deleteToolManagementState = { ...this.initialStateValue };
    this.getAllToolManagementState = { ...this.initialStateValue };
    this.fileUploadState = { ...this.initialStateValue };
    this.exportFileState = { ...this.initialStateValue };
    this.returnToolState = { ...this.initialStateValue };
    this.loanOutToolState = { ...this.initialStateValue };
    this.validateToolState = { ...this.initialStateValue };
    this.borrowToolState = { ...this.initialStateValue };
    this.toolLoanDetails = undefined;
    this.toolLoanDetailsState = { ...this.initialStateValue };
    this.importResults = [];
    this.base64String = { fileString: '' };
    this.addEditApplicatorMountingState = { ...this.initialStateValue };
    this.mainUnitOptions = [];
    this.mainUnitList = [];
    this.mainUnitOptionsState = { ...this.initialStateValue };
    this.unmountApplicatorState = { ...this.initialStateValue };
    this.trayIdsByTool = [];
    this.trayIdsByToolState = { ...this.initialStateValue };
    this.toolLedBlinkState = { ...this.initialStateValue };
  };
  //#endregion
}

export default new ToolManagementStore();
